<template>
  <div class="document-review-view">
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <div>Loading document...</div>
    </div>
    
    <div v-else-if="error" class="error-container">
      <div class="error-icon">⚠️</div>
      <div class="error-message">{{ error }}</div>
      <button @click="goBack" class="back-button">Go Back</button>
    </div>
    
    <div v-else class="review-container">
      <div class="review-header">
        <h2>Document Review</h2>
        <div class="document-info">
          <div class="document-type" v-if="documentType">
            <span class="label">Type:</span>
            <span class="value">{{ documentTypeName }}</span>
          </div>
          <button @click="goToClassification" class="classify-button">Classify Document</button>
        </div>
      </div>
      <DocumentReview
        :document-id="documentId"
        :document-image="documentImage"
        :extracted-fields="extractedFields"
        :document-type="documentType"
        @save="handleSave"
        @field-updated="handleFieldUpdate"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DocumentReview from '@/components/document-review/DocumentReview.vue';
import documentClassificationService from '@/services/documentClassificationService';
import documentReviewService from '@/services/documentReviewService';

const route = useRoute();
const router = useRouter();
const documentId = ref(route.params.id);
const documentImage = ref('');
const extractedFields = ref([]);
const documentType = ref(null);
const loading = ref(true);
const error = ref(null);

// Computed properties
const documentTypeName = computed(() => {
  if (!documentType.value) return 'Unknown';
  const typeInfo = documentClassificationService.getDocumentType(documentType.value);
  return typeInfo ? typeInfo.name : 'Unknown';
});

onMounted(async () => {
  try {
    // Fetch document data
    await fetchDocumentData();
    
    // Fetch document classification
    await fetchDocumentClassification();
  } catch (err) {
    console.error('Error loading document:', err);
    error.value = 'Failed to load document. Please try again later.';
  } finally {
    loading.value = false;
  }
});

async function fetchDocumentData() {
  try {
    // In a real implementation, this would fetch from your API using documentReviewService
    // For now, we'll simulate a delay and return mock data
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Mock document image URL (in production, this would come from your API)
    documentImage.value = documentReviewService.getDocumentImageUrl(documentId.value);
    
    // Mock extracted fields (in production, these would come from your API)
    extractedFields.value = [
      {
        id: '1',
        label: 'Project Name',
        value: '123 Main Street Development',
        confidence: 85,
        bounds: { x: 100, y: 150, width: 300, height: 30 }
      },
      {
        id: '2',
        label: 'Contract Amount',
        value: '$1,250,000.00',
        confidence: 75,
        bounds: { x: 400, y: 250, width: 150, height: 30 }
      },
      {
        id: '3',
        label: 'Contractor',
        value: 'ABC Construction Co.',
        confidence: 90,
        bounds: { x: 100, y: 200, width: 250, height: 30 }
      },
      {
        id: '4',
        label: 'Application Date',
        value: '2023-05-15',
        confidence: 65,
        bounds: { x: 500, y: 150, width: 120, height: 30 }
      },
      {
        id: '5',
        label: 'Period To',
        value: '2023-04-30',
        confidence: 25,
        bounds: { x: 500, y: 200, width: 120, height: 30 }
      },
      {
        id: '6',
        label: 'Retainage',
        value: '5%',
        confidence: 40,
        bounds: { x: 400, y: 300, width: 80, height: 30 }
      },
      {
        id: '7',
        label: 'Architect',
        value: 'XYZ Architects',
        confidence: 70,
        bounds: { x: 100, y: 250, width: 200, height: 30 }
      },
      {
        id: '8',
        label: 'Application Number',
        value: '3',
        confidence: 95,
        bounds: { x: 650, y: 150, width: 50, height: 30 }
      }
    ];
  } catch (err) {
    console.error('Error fetching document data:', err);
    throw err;
  }
}

async function fetchDocumentClassification() {
  try {
    // Try to get the document classification from the API
    const classification = await documentClassificationService.getClassification(documentId.value);
    
    if (classification && classification.type) {
      documentType.value = classification.type;
    } else {
      // If no classification exists, set to null
      documentType.value = null;
    }
  } catch (err) {
    console.error('Error fetching document classification:', err);
    // If there's an error, don't set the document type
    documentType.value = null;
  }
}

async function handleSave(data) {
  console.log('Saving changes:', data);
  
  try {
    // In a real implementation, you would send this data to your API
    // For now, we'll just simulate a successful save
    await documentReviewService.saveChanges(documentId.value, data.changes, data.auditTrail);
    
    // Show success message
    alert('Changes saved successfully!');
    
    // Optionally navigate away or refresh data
    // router.push('/documents');
  } catch (err) {
    console.error('Error saving changes:', err);
    alert('Failed to save changes. Please try again.');
  }
}

function handleFieldUpdate(field) {
  console.log('Field updated:', field);
  // You can implement real-time validation or other logic here
}

function goBack() {
  router.back();
}

function goToClassification() {
  router.push(`/document-classification/${documentId.value}`);
}
</script>

<style scoped>
.document-review-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.loading-container, .error-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #0366d6;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 48px;
  margin-bottom: 1rem;
}

.error-message {
  color: #d73a49;
  margin-bottom: 1rem;
}

.back-button {
  padding: 8px 16px;
  background-color: #0366d6;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.review-container {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f6f8fa;
  border-bottom: 1px solid #e1e4e8;
}

.review-header h2 {
  margin: 0;
}

.document-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.document-type {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: #f1f8ff;
  border-radius: 4px;
  font-size: 14px;
}

.document-type .label {
  font-weight: 500;
  color: #24292e;
}

.document-type .value {
  color: #0366d6;
}

.classify-button {
  padding: 8px 16px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.classify-button:hover {
  background-color: #218838;
}
</style>